import { useScreenClass, useIsScreenClassInitialized } from '@vp/swan'
import { useSelector } from 'react-redux'
import { useMemo } from 'react'
import { booleanRenderPropertySelector, stringRenderPropertySelector } from '~/app/store/config'
import { PURPOSE_NAMES, UsePersonalizationConfigOutput } from '~/app/features/Gallery/hooks/usePersonalizationConfig/interface'
import { useTranslations } from '~/app/features/Gallery/hooks/useTranslations'
import { RenderProperty } from 'shared/renderProperties'

export const usePersonalizationConfig = (): UsePersonalizationConfigOutput => {
  const screenClass = useScreenClass()
  const isScreenClassInitialized = useIsScreenClassInitialized()
  const localize = useTranslations()

  const booleanRenderProperty = useSelector(booleanRenderPropertySelector)
  const stringRenderPropSelector = useSelector(stringRenderPropertySelector)

  const isEnabled = booleanRenderProperty(RenderProperty.ShowPersonalizationUI)
  const imageCopyKey = stringRenderPropSelector(RenderProperty.PersonalizationImageCopy) || 'Photo'
  const templateTextFieldsString = stringRenderPropSelector(RenderProperty.PersonalizationTextFields) ?? ''

  const purposeNames = useMemo(
    () => templateTextFieldsString.split(',').filter((val): val is PURPOSE_NAMES => Object.values<string>(PURPOSE_NAMES).includes(val)),
    [templateTextFieldsString],
  )

  const templateFields = useMemo(() => {
    const purposePlaceholders = {
      [PURPOSE_NAMES.COMPANY_NAME]: localize('PersonalizationPlaceholderPurposeCompanyname'),
      [PURPOSE_NAMES.FAMILY_NAME]: localize('PersonalizationPlaceholderPurposeFamilyname'),
      [PURPOSE_NAMES.FULL_NAME]: localize('PersonalizationPlaceholderPurposeFullname'),
      [PURPOSE_NAMES.MAIN_TEXT]: localize('PersonalizationPlaceholderPurposeMainText'),
      [PURPOSE_NAMES.ADDITIONAL_TEXT]: localize('PersonalizationPlaceholderPurposeAdditionalText'),
    }

    return purposeNames.map((purposeName) => ({
      purposeName,
      placeholder: purposePlaceholders[purposeName],
    }))
  }, [localize, purposeNames])

  const isSmallScreen = useMemo(() => {
    const smallScreenClasses = ['xs']

    return isScreenClassInitialized && smallScreenClasses.includes(screenClass)
  }, [screenClass, isScreenClassInitialized])

  return {
    isEnabled,
    mode: isSmallScreen ? 'modal' : 'sidebar',
    imageCopyKey,
    templateFields,
  }
}
